'use client';

/**
 * Bell icon button with a soft popover panel — ported from
 * ThemeMaker's {@code lms-portal/src/components/shell/NotificationsBell.tsx}.
 *
 * <p>Used by every header style with {@code header.showNotifications:
 * true} in shell config.
 *
 * <h3>Host adapter</h3>
 * Data comes from {@link useNotificationsSource}. When no source is
 * mounted (host hasn't wired notifications yet), the widget renders
 * nothing — the bell only appears once the host plugs in real data,
 * so a chrome doesn't show an inert bell.
 *
 * <h3>Behaviour</h3>
 * <ul>
 *   <li>Bell badge from {@code fetchUnreadCount()}, polled every 60s
 *       while the page is visible. Push-based updates are additive —
 *       hosts that ship a WebSocket can swap polling for a subscription
 *       in their {@link NotificationsSource} impl.</li>
 *   <li>First open fetches page 1.</li>
 *   <li>Items grouped by date bucket: Today / This week / Earlier.</li>
 *   <li>"Load more" button at the bottom when {@code nextCursor} is
 *       non-null.</li>
 *   <li>Each row click marks-read (optimistic), navigates if
 *       {@code href} set.</li>
 *   <li>"Mark all read" header button when any unread.</li>
 *   <li>Click outside / Escape closes.</li>
 *   <li>Theme-aware via tokens. Locale-aware labels + relative time.</li>
 *   <li>{@code inverse} for dark-chrome headers.</li>
 * </ul>
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import Link from 'next/link';
import type { CSSProperties } from 'react';
import {
  NOTIFICATIONS_REFRESH_EVENT,
  useNotificationsSource,
  type NotificationItem,
} from '../../portal';

const PAGE_SIZE = 10;
const POLL_MS = 60_000;

export interface NotificationsBellProps {
  /** Locale for "x time ago" formatting and labels. */
  locale?: string;
  /** Light icon styling for dark-chrome headers. */
  inverse?: boolean;
}

export function NotificationsBell({ locale = 'en', inverse }: NotificationsBellProps) {
  const source = useNotificationsSource();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const popoverId = useId();

  // Background poll for unread count. Cheap endpoint; doesn't pull
  // the full list. Stops when the tab is hidden so backgrounded tabs
  // don't burn requests.
  useEffect(() => {
    if (!source) return;
    let stopped = false;
    const ac = new AbortController();
    const tick = async () => {
      if (stopped) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try {
        const count = await source.fetchUnreadCount(ac.signal);
        if (!stopped) setUnreadCount(count);
      } catch {
        /* swallow */
      }
    };
    void tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      ac.abort();
      window.clearInterval(id);
    };
  }, [source]);

  // First-open fetch.
  useEffect(() => {
    if (open && !hasFetched && source) {
      void fetchFirstPage();
    }
  }, [open, source]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const fetchFirstPage = useCallback(async () => {
    if (!source) return;
    setLoading(true);
    try {
      const page = await source.fetchPage({ limit: PAGE_SIZE });
      setItems(Array.isArray(page.items) ? page.items : []);
      setNextCursor(page.nextCursor ?? null);
      if (typeof page.unreadCount === 'number') setUnreadCount(page.unreadCount);
      setHasFetched(true);
    } catch {
      /* swallow */
    } finally {
      setLoading(false);
    }
  }, [source]);

  // Any push message on the host's event bus (points, badges, comments,
  // approvals, ...) means a notification was just created server-side —
  // refresh the unread count immediately instead of waiting for the next
  // poll tick, and re-pull page 1 if the list has already been fetched so
  // an open panel shows the new item live.
  useEffect(() => {
    if (!source) return;
    const onRefresh = () => {
      void source
        .fetchUnreadCount()
        .then(setUnreadCount)
        .catch(() => {});
      if (hasFetched) void fetchFirstPage();
    };
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, onRefresh);
  }, [source, hasFetched, fetchFirstPage]);

  async function loadMore() {
    if (!source || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await source.fetchPage({ cursor: nextCursor, limit: PAGE_SIZE });
      const more = Array.isArray(page.items) ? page.items : [];
      setItems((cur) => [...cur, ...more]);
      setNextCursor(page.nextCursor ?? null);
    } catch {
      /* swallow */
    } finally {
      setLoadingMore(false);
    }
  }

  async function markRead(id: string) {
    if (!source) return;
    setItems((cur) => cur.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await source.markRead(id);
    } catch {
      /* swallow */
    }
  }

  async function markAllRead() {
    if (!source) return;
    setItems((cur) => cur.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await source.markAllRead();
    } catch {
      /* swallow */
    }
  }

  // No source mounted — don't render an inert bell. Hosts that lack
  // notifications get a chrome without a bell rather than a bell that
  // does nothing on click.
  if (!source) return null;

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={tr('notifications', locale)}
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => setOpen((v) => !v)}
        style={{
          ...iconButtonBase,
          // Inverse mode uses the on-sunken triplet so the bell stays
          // visible against fixed-dark chrome (BTK pattern). Default
          // mode uses text-primary so the icon reads at full strength.
          color: inverse
            ? 'var(--color-text-on-sunken, #ffffff)'
            : 'var(--color-text-primary, #18181b)',
          border: inverse
            ? '1px solid var(--color-border-on-sunken, rgba(255,255,255,0.18))'
            : '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
        }}
      >
        <BellIcon size={16} />
        {unreadCount > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -8,
              right: -8,
              minWidth: 23,
              height: 23,
              padding: '4px',
              borderRadius: 999,
              background: 'var(--color-danger-fg, #ef4444)',
              border: '2px solid var(--color-surface-page, #0a0a0a)',
              color: '#ffffff',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          id={popoverId}
          role="dialog"
          aria-label={tr('notifications', locale)}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 'min(380px, calc(100vw - 24px))',
            maxHeight: '70vh',
            background: 'var(--color-surface-card, #141414)',
            border: '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))',
            borderRadius: 'var(--radius-md, 12px)',
            boxShadow: 'var(--shadow-xl, 0 24px 48px rgba(0,0,0,0.18))',
            color: 'var(--color-text-primary, #ffffff)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 70,
            animation: 'btk-popover-in 160ms ease-out',
          }}
        >
          {/* Header row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-3-5, 14px) var(--space-4, 16px)',
              borderBottom: '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-sm, 14px)', fontWeight: 600 }}>
              {tr('notifications', locale)}
              {unreadCount > 0 && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: 'var(--space-px, 1px) var(--space-2, 8px)',
                    borderRadius: 999,
                    background: 'var(--color-brand-500, #ffffff)',
                    color: 'var(--color-text-inverse, #0a0a0a)',
                    letterSpacing: 'var(--font-tracking-wide, 0.02em)',
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-text-secondary, rgba(255,255,255,0.65))',
                  fontSize: 'var(--font-size-xs, 12px)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                {tr('markAllRead', locale)}
              </button>
            )}
          </div>

          {/* Body — scrollable flat list with infinite scroll */}
          <div
            ref={panelRef}
            className="btk-notif-scroll"
            style={{
              overflowY: 'auto',
              maxHeight: items.length > 8 ? 520 : 300,
            }}
            onScroll={async (e) => {
              const el = e.currentTarget;
              if (
                el.scrollTop + el.clientHeight >= el.scrollHeight - 8 &&
                nextCursor &&
                !loadingMore
              ) {
                await loadMore();
              }
            }}
          >
            {loading && items.length === 0 && (
              <div style={{ padding: 'var(--space-3, 12px) var(--space-4, 16px)' }}>
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: 'var(--space-2-5, 10px) 0',
                      borderBottom: '1px solid var(--color-border-subtle, rgba(255,255,255,0.06))',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        height: 13,
                        borderRadius: 6,
                        background: 'var(--color-surface-elevated, rgba(255,255,255,0.08))',
                        animation: 'btk-skeleton 1.2s ease-in-out infinite',
                      }}
                    />
                    <div
                      style={{
                        width: 48,
                        height: 13,
                        borderRadius: 6,
                        background: 'var(--color-surface-elevated, rgba(255,255,255,0.08))',
                        animation: 'btk-skeleton 1.2s ease-in-out infinite',
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            {!loading && items.length === 0 && (
              <div style={emptyStyle}>{tr('noNotifications', locale)}</div>
            )}
            {items.map((n) => (
              <NotificationRow
                key={n.id}
                n={n}
                locale={locale}
                onActivate={() => {
                  if (!n.read) void markRead(n.id);
                  setOpen(false);
                }}
              />
            ))}
            {loadingMore && (
              <div
                style={{
                  padding: 'var(--space-2-5, 10px) 0',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <SpinnerIcon />
              </div>
            )}
          </div>

          {/* Footer — See all link */}
          {!loading && !loadingMore && (
            <div
              style={{
                borderTop: '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))',
                padding: 'var(--space-2-5, 10px) var(--space-4, 16px)',
                textAlign: 'center',
              }}
            >
              <Link
                href="/notifications"
                onClick={() => setOpen(false)}
                style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--color-text-secondary, rgba(255,255,255,0.65))',
                  textDecoration: 'none',
                }}
              >
                {tr('seeAll', locale)}
              </Link>
            </div>
          )}
        </div>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes btk-popover-in {
              from { opacity: 0; transform: translateY(-4px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            @keyframes btk-spin {
              to { transform: rotate(360deg); }
            }
            @keyframes btk-skeleton {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.4; }
            }
            .btk-notif-scroll {
              scrollbar-width: thin;
              scrollbar-color: var(--color-border-default, rgba(255,255,255,0.18)) transparent;
            }
            .btk-notif-scroll::-webkit-scrollbar { width: 6px; }
            .btk-notif-scroll::-webkit-scrollbar-track { background: transparent; }
            .btk-notif-scroll::-webkit-scrollbar-thumb {
              background: var(--color-border-default, rgba(255,255,255,0.18));
              border-radius: 999px;
            }
          `,
        }}
      />
    </div>
  );
}

function NotificationRow({
  n,
  locale,
  onActivate,
}: {
  n: NotificationItem;
  locale: string;
  onActivate: () => void;
}) {
  const Icon = iconForKind(n.kind);
  const inner = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 12,
        padding: 'var(--space-3, 12px) var(--space-4, 16px)',
        borderBottom: '1px solid var(--color-border-subtle, rgba(255,255,255,0.06))',
        cursor: n.href ? 'pointer' : 'default',
        transition: 'background 120ms ease',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'var(--color-surface-elevated, #1a1a1a)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: 'var(--color-text-primary, #ffffff)',
          overflow: 'hidden',
        }}
      >
        {n.iconUrl ? (
          <>
            <img
              src={n.iconUrl}
              alt=""
              style={{ width: 32, height: 32, objectFit: 'cover' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const sibling = e.currentTarget.nextElementSibling as HTMLElement | null;
                if (sibling) sibling.style.display = 'flex';
              }}
            />
            <span style={{ display: 'none', alignItems: 'center', justifyContent: 'center' }}>
              <Icon size={14} />
            </span>
          </>
        ) : (
          <Icon size={14} />
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: '13px',
            fontWeight: n.read ? 400 : 600,
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {n.label ? (
            <n.label />
          ) : (
            <>
              {n.title}
              {n.body ? ` — ${n.body}` : ''}
            </>
          )}
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: '11px',
            color: 'var(--color-text-tertiary, rgba(255,255,255,0.45))',
          }}
        >
          {relTime(n.createdAt, locale)}
        </div>
      </div>
      {!n.read && (
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'var(--color-brand-500, #ffffff)',
            marginTop: 12,
            flexShrink: 0,
          }}
          aria-hidden
        />
      )}
    </div>
  );

  return n.href ? (
    <Link
      href={n.href}
      onClick={onActivate}
      style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
      onMouseEnter={(e) => {
        (e.currentTarget.firstElementChild as HTMLElement).style.background =
          'var(--color-surface-elevated, rgba(255,255,255,0.04))';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget.firstElementChild as HTMLElement).style.background = 'transparent';
      }}
    >
      {inner}
    </Link>
  ) : (
    <div onClick={onActivate}>{inner}</div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/** Built-in dictionary for the handful of bell-internal labels.
 *  Renamed from {@code t} to {@code tr} to avoid colliding with the
 *  platform {@code t()} — these are widget-internal labels, not
 *  per-tenant config. */
function tr(key: string, locale: string): string {
  const dict: Record<string, Record<string, string>> = {
    notifications: { tr: 'Bildirimler', en: 'Notifications' },
    markAllRead: { tr: 'Hepsini okundu işaretle', en: 'Mark all read' },
    loading: { tr: 'Yükleniyor...', en: 'Loading…' },
    noNotifications: { tr: 'Henüz bildirimin yok.', en: 'No notifications yet.' },
    loadingMore: { tr: 'Yükleniyor...', en: 'Loading…' },
    seeAll: { tr: 'Tümünü Gör', en: 'See all' },
  };
  return dict[key]?.[locale] ?? dict[key]?.['en'] ?? key;
}

function relTime(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.floor((now - then) / 1000));
  const labels =
    locale === 'tr'
      ? { now: 'şimdi', m: 'd önce', h: 's önce', d: 'g önce', w: 'h önce' }
      : { now: 'just now', m: 'm ago', h: 'h ago', d: 'd ago', w: 'w ago' };
  if (sec < 60) return labels.now;
  if (sec < 3600) return `${Math.floor(sec / 60)}${labels.m}`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}${labels.h}`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}${labels.d}`;
  return `${Math.floor(sec / (86400 * 7))}${labels.w}`;
}

function iconForKind(kind: string) {
  switch (kind) {
    case 'course_update':
      return BookIcon;
    case 'achievement':
      return TrophyIcon;
    case 'announcement':
      return MegaphoneIcon;
    case 'reminder':
      return ClockIcon;
    default:
      return BellIcon;
  }
}

const iconButtonBase: CSSProperties = {
  position: 'relative',
  width: 36,
  height: 36,
  borderRadius: '50%',
  background: 'transparent',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  outline: 'none',
};

const emptyStyle: CSSProperties = {
  padding: 'var(--space-8, 32px) var(--space-4, 16px)',
  fontSize: '13px',
  color: 'var(--color-text-secondary, rgba(255,255,255,0.55))',
  textAlign: 'center',
};

// ---- Inline icons (lucide geometry, no Lucide dep to keep widget light) ----

function BellIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 2 6.5H4c.5-1 2-2.5 2-6.5z" />
      <path d="M10 18a2 2 0 0 0 4 0" />
    </svg>
  );
}
function BookIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5v14z" />
    </svg>
  );
}
function TrophyIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4zM7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3" />
    </svg>
  );
}
function MegaphoneIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 11v2a2 2 0 0 0 2 2h2l4 4V5L7 9H5a2 2 0 0 0-2 2zM15 8a4 4 0 0 1 0 8" />
    </svg>
  );
}
function ClockIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
function SpinnerIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden
      style={{ animation: 'btk-spin 0.8s linear infinite' }}
    >
      <path d="M12 3a9 9 0 1 0 9 9" />
    </svg>
  );
}
